<template>
  <base-modal :ref="modal.key" :show="open" :title="modalTitle" @on-cancel="hide()" :maxWidth="modalWidth"
    :showConfirmButton="false">
    <div class="mermaid-graph-loading" v-if="loading">
        <v-progress-circular
        color="primary"
        size="60"
        class="mt-10"
        indeterminate
        ></v-progress-circular>
    </div>
    <div style="overflow-x: hidden;">
        <div v-if="diagrams.length == 1" class="mt-2">
            <pre class="mermaid" v-html="graph" :ref="`${mermaidDiagramKey}0`"></pre>
        </div>
        <div v-if="diagrams.length > 1">
            <v-tabs centered v-model="selectedTab" @change="onChangeTab">
                <v-tab v-for="(diagram, index) in diagrams" :key="index">{{ diagram.flowName }}</v-tab>
                <v-tab-item v-for="(diagram, index) in diagrams" :key="index" class="mt-2" :eager="true">
                    <pre class="mermaid" v-html="graph" :ref="`${mermaidDiagramKey}${index}`"></pre>
                </v-tab-item>
            </v-tabs>
        </div>
    </div>
      <template v-slot:extra>
        <div class="states-info-container">
          <div class="states-info-header">
            <div v-if="fromDocument">
                <span class="ml-2"><v-icon class="mr-1" color="#C8E6C9" small>mdi-square</v-icon>{{ $t("Executed") }}</span>
                <span class="ml-2"><v-icon class="mr-1" color="#BBDEFB" small>mdi-square</v-icon>{{ $t("InProgress") }}</span>
            </div>
            <template v-if="unconnectedStates.length > 0">
                <v-icon small class="ml-2 mr-1" color="warning">mdi-alert-circle-outline</v-icon>
                <span>{{ $t("Site.Flows.UnconnectedStates") }} ({{ unconnectedStates.length }}):</span>
            </template>
          </div>
          <div class="unconnected-states-container" v-if="unconnectedStates.length > 0">
              <v-chip v-for="state in unconnectedStates"
                :key="state.id"
                x-small
                outlined
                class="ma-1"
                color="warning"
              >
                {{ state.name }}
              </v-chip>
          </div>
        </div>
    </template>
  </base-modal>
</template>
<script>
import FlowDiagram from '@/ecm/models/workflow/FlowDiagram';
import { FlowTransitionStateTypes } from '@/ecm/models/workflow/FlowConstants';
import svgPanZoom from 'svg-pan-zoom';

export default {
    name: 'FlowDiagramModal',
    data() {
        return {
            modal: {
                title: this.$t('FlowChart'),
                key: 'flowDiagramModal',
            },
            open: false,
            fromDocument: false,
            diagrams: [],
            selectedTab: 0,
            mermaidDiagramKey: 'mermaidDiagram',
            graph: null,
            loading: false,
            unconnectedStates: [],
            panZoomKey: '',
            panZoomInstances: {}
        };
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
        modalTitle() {
            return this.diagrams.length === 1
                ? this.$t('FlowChart') + ' - ' + this.diagrams[0].flowName
                : this.$t('FlowChart');
        },
        modalWidth() {
            return '70vw';
        }
    },
    methods: {
        generateDiagram(diagram) {
            this.graph = null;
            this.unconnectedStates = [];
            let mermaidDiagram = 'stateDiagram-v2\n\r';
            mermaidDiagram += this.buildClasses();
            const mermaidStates = new Map();
            const approvers = new Map();
            const currentStates = new Map();

            diagram.nodes.forEach(state => {
                mermaidStates.set(state.id, state);
                currentStates.set(state.id, state.currentState);

                if (state.approvers && state.approvers.length > 0 && state.approvers[0].name !== '') {
                    approvers.set(state.id, state.approvers);
                }
            });

            const statesToHide = new Set(mermaidStates.keys());

            diagram.connections.forEach(con => {
                statesToHide.delete(con.source.id);
                statesToHide.delete(con.destination.id);
            });
            statesToHide.forEach(id => {
                this.unconnectedStates.push(mermaidStates.get(id));
                mermaidStates.delete(id);
            });

            mermaidDiagram += this.buildStateDefinition(mermaidStates, approvers, currentStates);
            mermaidDiagram += this.buildStateConnections(diagram.connections);

            this.graph = mermaidDiagram;
        },
        buildStateConnections(connections) {
            if (connections === null) {
                return;
            }
            let connectionsString = '';
            connections.forEach(con => {
                connectionsString += `\n s${con.id} --> s${con.destination.id}:  ${this.$t(con.type)}`;
            });
            return connectionsString;
        },
        buildStateDefinition(states, approvers, currentStates) {
            if (!states) return;

            let stateDefinitions = '\n';
            let stateClass;

            states.forEach((state, id) => {
                if (currentStates.has(id)) {
                    switch (currentStates.get(id)) {
                    case FlowTransitionStateTypes.InProcess: {
                        stateClass = FlowTransitionStateTypes.InProcess;
                        break;
                    }
                    case FlowTransitionStateTypes.Executed: {
                        stateClass = FlowTransitionStateTypes.Executed;
                        break;
                    }
                    default: stateClass = FlowTransitionStateTypes.Default;
                    }
                }
                stateDefinitions += `\n s${id}:::${stateClass} : ${state.name}`;

                if (approvers && approvers.has(id)) {
                    const approversForState = approvers.get(id);
                    if (state.hasMultiValidation) {
                        const latestApprovalsByName = new Map();

                        approversForState.forEach(approver => {
                            const key = approver.name;
                            const currentDate = new Date(approver.date);
                            if (!latestApprovalsByName.has(key) || currentDate > new Date(latestApprovalsByName.get(key).date)) {
                                latestApprovalsByName.set(key, approver);
                            }
                        });

                        latestApprovalsByName.forEach(approver => {
                            stateDefinitions += `\n s${id}: ${approver.date.replace(/:/g, '#colon;')}hs (${approver.name})`;
                        });
                    } else {
                        // Replaces ":" with special word #colon; to avoid mermaid syntax error
                        stateDefinitions += `\n s${id}:${approvers.get(id)[0].date.replace(/:/g, '#colon;')}hs (${approvers.get(id)[0].name})`;
                    }
                }
                if (state.validationsRemaining > 0 && stateClass === FlowTransitionStateTypes.InProcess && state.hasMultiValidation)
                    stateDefinitions += `\n s${id}: ${this.$tc('Site.Flows.ValidationsRemaining', state.validationsRemaining, { n: state.validationsRemaining })}`;
            });

            return stateDefinitions;
        },
        buildClasses() {
            return 'classDef DefaultState fill:white, stroke:#bcbcbc,text-align: center,align-content: center\n' +
                'classDef Executed fill:#C8E6C9, font-weight:bold, stroke:#97bc99,text-align: center,align-content: center\n' +
                'classDef InProcess fill:#BBDEFB, font-weight:bold, stroke:#6c8ca7,text-align: center,align-content: center\n';
        },
        fromFlow(flowName, flowId, states) {
            const diagramFlow = new FlowDiagram();
            diagramFlow.flowId = flowId;
            diagramFlow.buildFrom(states, (document.body.clientWidth - 130) * 0.7);
            diagramFlow.flowName = flowName;

            this.diagrams = [diagramFlow];
            this.fromDocument = false;
            this.generateDiagram(diagramFlow);

            this.show();
        },

        fromDocumentList(flowDiagramList) {
            this.fromDocument = true;
            const diagram = flowDiagramList.map((x) => new FlowDiagram(x));

            this.diagrams = diagram;
            this.generateDiagram(diagram[this.selectedTab]);

            this.show();
        },
        show() {
            this.open = true;
            this.selectedTab = 0;
            this.initMermaidDiagram();
        },
        hide() {
            this.saveSvgPanZoomState();
            this.open = false;
            this.selectedTab = 0;
            this.graph = null;
        },
        onChangeTab(tabNumber) {
            this.saveSvgPanZoomState();
            this.selectedTab = tabNumber;
            this.generateDiagram(this.diagrams[this.selectedTab]);
            this.initMermaidDiagram();
        },
        async drawDiagram() {
            window.mermaid.initialize({ startOnLoad: true, useMaxWidth: true, maxWidth: '100%', theme: 'default' });

            const mermaidGraphsData = document.getElementsByClassName('mermaid');
            const currentMermaidDiagram = `${this.mermaidDiagramKey}${this.selectedTab}`;

            const { svg } = await window.mermaid.render(currentMermaidDiagram, mermaidGraphsData[this.selectedTab].innerText);
            if (!svg) return;
            mermaidGraphsData[this.selectedTab].innerHTML = svg;

            const mermaidDiagram = Array.isArray(this.$refs[currentMermaidDiagram])
                ? this.$refs[currentMermaidDiagram][0]
                : this.$refs[currentMermaidDiagram];
            const svgElement = mermaidDiagram.querySelector('svg');
            if (!svgElement) return;

            svgElement.setAttribute('width', '100%');
            svgElement.setAttribute('height', '100%');
            svgElement.style.maxWidth = '100%';
            svgElement.style.width = '100%';

            this.panZoomKey = `#${currentMermaidDiagram}`;
            const panZoomInstance = svgPanZoom(this.panZoomKey, {
                zoomEnabled: true,
                controlIconsEnabled: true,
                fit: true,
                center: true,
                minZoom: 0.2,
                maxZoom: 10,
            });

            this.panZoomInstances[this.selectedTab] = panZoomInstance;
            this.restorePanZoomState(panZoomInstance);
        },
        initMermaidDiagram() {
            this.loading = true;
            this.$nextTick(async () => {
                await this.drawDiagram();
                this.loading = false;
            });
        },
        saveSvgPanZoomState() {
            const diagramId = this.diagrams[this.selectedTab].flowId;
            const panZoomInstance = this.panZoomInstances[this.selectedTab];
            if (!panZoomInstance) return;

            localStorage.setItem(`diagramPanZoom_${diagramId}`, JSON.stringify({
                pan: panZoomInstance.getPan(),
                zoom: panZoomInstance.getZoom()
            }));
        },
        restorePanZoomState(panZoomInstance) {
            const diagramId = this.diagrams[this.selectedTab].flowId;
            const savedPanZoomState = localStorage.getItem(`diagramPanZoom_${diagramId}`);
            if (savedPanZoomState) {
                const { pan, zoom } = JSON.parse(savedPanZoomState);
                panZoomInstance.zoom(zoom);
                panZoomInstance.pan(pan);
            }
        }
    }
};
</script>
<style scoped>
.mermaid {
    width: 100% !important;
    height: 67vh !important;
    line-height: 1.2em !important;
    background: #f8f8f8;
    background-image: radial-gradient(#c4c4c4 5%, transparent 0);
    background-size: 20px 20px;
    border-radius: 5px;
    overflow: hidden;
    cursor: grab;
}
.mermaid:active {
    cursor: grabbing;
}
.mermaid-graph-loading {
    position: absolute;
    width: 95%;
    height: 85%;
    text-align: center;
    background-color: white;
}
.states-info-container {
  width: 90%;
  max-width: 90%;
}
.states-info-header {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  padding-top: 0px;
}
.unconnected-states-container {
  max-height: 60px;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  margin-left: 16px;
}
.unconnected-states-container::-webkit-scrollbar {
  width: 4px;
}
.unconnected-states-container::-webkit-scrollbar-track {
  background: transparent;
}
.unconnected-states-container::-webkit-scrollbar-thumb {
  background: #a1a1a1;
  border-radius: 2px;
}
</style>
